import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BriefcaseBusiness,
  Mail,
  MapPin,
  MessageSquare,
  RotateCcw,
  Send,
  Twitter,
} from 'lucide-react';

const contactLinks = [
  {
    label: 'Business',
    value: 'simon@6uo.io',
    href: 'mailto:simon@6uo.io',
    icon: BriefcaseBusiness,
  },
  {
    label: 'Say hello',
    value: 'hello@simonlokc.com',
    href: 'mailto:hello@simonlokc.com',
    icon: Mail,
  },
];

const socialLinks = [
  {
    label: 'X',
    detail: '@simonlokc',
    href: 'https://x.com/simonlokc',
    icon: Twitter,
  },
  {
    label: 'Discord',
    detail: '6uo community',
    href: 'https://discord.gg/38edatMB8E',
    icon: MessageSquare,
  },
  {
    label: 'Telegram',
    detail: '@simonlokc',
    href: 'https://t.me/simonlokc',
    icon: Send,
  },
];

const Home = () => {
  const [isFlipped, setIsFlipped] = useState(false);

  const toggleCard = () => setIsFlipped((current) => !current);
  const getInactiveFaceProps = (isInactive) => ({
    'aria-hidden': isInactive,
    inert: isInactive ? '' : undefined,
  });

  const handleCardClick = (event) => {
    if (event.target.closest('a, button')) {
      return;
    }

    toggleCard();
  };

  return (
    <main className="min-h-screen overflow-hidden bg-[#09111b] text-white">
      <section className="relative flex min-h-screen items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_10%,rgba(0,240,255,0.16),transparent_34%),linear-gradient(135deg,rgba(12,24,38,0.95),rgba(5,10,16,1)_62%)]" />
        <div className="absolute left-1/2 top-1/2 h-[min(76vw,560px)] w-[min(76vw,560px)] -translate-x-1/2 -translate-y-1/2 rounded-full border border-neon-blue/10 bg-neon-blue/5 blur-2xl" />

        <motion.div
          initial={{ opacity: 0, y: 26, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          whileHover={{ y: -6, scale: 1.01 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          onClick={handleCardClick}
          className="group/card relative z-10 w-full max-w-[900px] cursor-pointer card-perspective"
          aria-label="Profile card. Click blank space to flip."
        >
          <div
            className={`relative min-h-[650px] w-full transition-transform duration-700 card-preserve-3d md:min-h-[560px] ${
              isFlipped ? 'card-flipped' : ''
            }`}
          >
            <article
              data-testid="profile-card-front"
              className="absolute inset-0 flex flex-col justify-between overflow-hidden rounded-[28px] border border-white/12 bg-[#0d1824]/92 p-6 shadow-[0_30px_120px_rgba(0,0,0,0.55),0_0_42px_rgba(0,240,255,0.18)] backdrop-blur-xl transition duration-500 group-hover/card:border-neon-blue/45 group-hover/card:shadow-[0_34px_130px_rgba(0,0,0,0.62),0_0_58px_rgba(0,240,255,0.26)] card-face sm:p-8 md:p-10"
              {...getInactiveFaceProps(isFlipped)}
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon-blue/80 to-transparent transition-opacity duration-500 group-hover/card:opacity-100 opacity-70" />
              <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-neon-blue/10 blur-3xl transition duration-500 group-hover/card:bg-neon-blue/16" />

              <div className="relative flex items-start justify-between gap-4">
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-neon-blue/25 bg-white/8 p-2 shadow-[0_0_24px_rgba(0,240,255,0.18)]">
                  <img
                    src="/simon-icon.png"
                    alt="Simon Lo icon"
                    className="h-full w-full object-contain"
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleCard}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/7 text-cyber-gray transition hover:rotate-[-18deg] hover:border-neon-blue/50 hover:text-white group-hover/card:border-neon-blue/35 group-hover/card:text-white focus:outline-none focus:ring-2 focus:ring-neon-blue/70"
                  aria-label="Show links on the back of the card"
                >
                  <RotateCcw className="h-5 w-5" />
                </button>
              </div>

              <div className="relative py-7 md:max-w-2xl md:py-8">
                <p className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.22em] text-neon-blue">
                  <MapPin className="h-4 w-4" />
                  Hong Kong
                </p>
                <h1 className="font-display text-5xl font-black leading-[0.92] tracking-normal text-white sm:text-6xl md:text-7xl">
                  SIMON
                  <span className="block text-white/76 md:inline md:pl-4">LO</span>
                </h1>
                <p className="mt-5 text-xl font-semibold text-white">
                  Co-founder, 6uo Games
                </p>
                <p className="mt-4 max-w-md text-base leading-7 text-cyber-gray sm:text-lg">
                  Building custom apps, games, Shopify agents, and SEO systems for modern online businesses.
                </p>
              </div>

              <div className="relative grid gap-5 md:grid-cols-[1fr_auto] md:items-stretch">
                <div className="grid gap-3 md:grid-cols-2">
                  {contactLinks.map(({ label, value, href, icon: Icon }) => (
                    <a
                      key={href}
                      href={href}
                      className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.045] px-4 py-3 transition hover:border-neon-blue/45 hover:bg-neon-blue/10 focus:outline-none focus:ring-2 focus:ring-neon-blue/70"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/8 text-neon-blue">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-cyber-gray">
                          {label}
                        </span>
                        <span className="block truncate text-sm font-semibold text-white sm:text-base">
                          {value}
                        </span>
                      </span>
                    </a>
                  ))}
                </div>

                <a
                  href="/6uo"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-4 text-base font-bold text-[#09111b] shadow-[0_0_28px_rgba(255,255,255,0.22)] transition hover:bg-neon-blue hover:text-[#031018] focus:outline-none focus:ring-2 focus:ring-neon-blue/80 md:min-h-[66px] md:w-56 md:rounded-2xl md:py-0"
                >
                  Explore 6uo
                  <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
                </a>
              </div>
            </article>

            <article
              data-testid="profile-card-back"
              className="absolute inset-0 flex flex-col overflow-hidden rounded-[28px] border border-neon-blue/25 bg-[#07131f]/95 p-6 shadow-[0_30px_120px_rgba(0,0,0,0.58),0_0_46px_rgba(0,240,255,0.22)] backdrop-blur-xl transition duration-500 group-hover/card:border-neon-blue/55 group-hover/card:shadow-[0_34px_130px_rgba(0,0,0,0.64),0_0_60px_rgba(0,240,255,0.3)] card-back card-face sm:p-8 md:p-10"
              {...getInactiveFaceProps(!isFlipped)}
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent transition-opacity duration-500 opacity-70 group-hover/card:opacity-100" />

              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-neon-blue">
                    Social
                  </p>
                  <h2 className="mt-2 font-display text-3xl font-black tracking-normal text-white md:mt-3 md:text-5xl">
                    Connect
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={toggleCard}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/7 text-cyber-gray transition hover:rotate-[-18deg] hover:border-neon-blue/50 hover:text-white group-hover/card:border-neon-blue/35 group-hover/card:text-white focus:outline-none focus:ring-2 focus:ring-neon-blue/70"
                  aria-label="Show profile details on the front of the card"
                >
                  <RotateCcw className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-5 grid flex-1 content-center gap-3 pr-1 md:mt-7 md:grid-cols-3">
                {socialLinks.map(({ label, detail, href, icon: Icon }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex min-h-[150px] flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.045] p-4 transition hover:border-neon-blue/45 hover:bg-neon-blue/10 focus:outline-none focus:ring-2 focus:ring-neon-blue/70"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/8 text-neon-blue transition group-hover:bg-neon-blue group-hover:text-[#031018]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-base font-bold text-white">
                        {label}
                      </span>
                      <span className="block truncate text-sm text-cyber-gray">
                        {detail}
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-cyber-gray transition group-hover:translate-x-1 group-hover:text-white" />
                  </a>
                ))}
              </div>
            </article>
          </div>

          <p className="mt-6 text-center text-xs font-semibold uppercase tracking-[0.2em] text-cyber-gray/70">
            Click or tap rotate to flip
          </p>
        </motion.div>
      </section>
    </main>
  );
};

export default Home;
